import React, { createContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import posthog from 'posthog-js';
import { Amplify } from 'aws-amplify';
import {
  signIn as amplifySignIn,
  signUp as amplifySignUp,
  signOut as amplifySignOut,
  confirmSignUp as amplifyConfirmSignUp,
  autoSignIn as amplifyAutoSignIn,
  confirmSignIn as amplifyConfirmSignIn,
  resetPassword as amplifyResetPassword,
  confirmResetPassword as amplifyConfirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
  resendSignUpCode as amplifyResendSignUpCode,
} from 'aws-amplify/auth';

// Configure Amplify with Cognito settings from environment variables
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
    },
  },
});

// Auth context - provides auth state to the entire app
export const AuthContext = createContext(null);

/**
 * CognitoAuthProvider
 * 
 * Wraps the app and provides authentication state + actions via context.
 * Manages Cognito session, auto-refresh, and user state.
 * Stores tokens in localStorage for persistence across browser sessions.
 */
const CognitoAuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const refreshTimerRef = useRef(null);

  /**
   * Identify the user in PostHog using phone_number as the distinct ID
   */
  const identifyPostHogUser = useCallback((userObj) => {
    if (!userObj?.phone_number) return;
    try {
      posthog.identify(userObj.phone_number, {
        phone_number: userObj.phone_number,
        name: `${userObj.given_name} ${userObj.family_name}`.trim() || undefined,
        cognito_sub: userObj.sub,
        groups: userObj.groups,
      });
    } catch (err) {
      console.error('PostHog identify failed:', err);
    }
  }, []);

  /**
   * Build a normalized user object from Cognito session data
   */
  const buildUserObject = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      if (!session?.tokens) return null;

      const idToken = session.tokens.idToken;
      const payload = idToken?.payload || {};

      return {
        sub: payload.sub,
        given_name: payload.given_name || '',
        family_name: payload.family_name || '',
        phone_number: payload.phone_number || '',
        email: payload.email || '',
        groups: payload['cognito:groups'] || [],
      };
    } catch {
      return null;
    }
  }, []);

  /**
   * Schedule a token refresh before the access token expires
   */
  const scheduleTokenRefresh = useCallback(async () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
    }

    try {
      const session = await fetchAuthSession();
      if (!session?.tokens?.accessToken) return;

      const exp = session.tokens.accessToken.payload.exp;
      if (!exp) return;

      // Refresh 5 minutes before expiry
      const now = Math.floor(Date.now() / 1000);
      const refreshIn = Math.max((exp - now - 300) * 1000, 60000); // At least 1 minute

      refreshTimerRef.current = setTimeout(async () => {
        try {
          // fetchAuthSession will auto-refresh if token is expired
          await fetchAuthSession({ forceRefresh: true });
          const newUser = await buildUserObject();
          if (newUser) {
            setUser(newUser);
            scheduleTokenRefresh();
          }
        } catch (err) {
          console.error('Token refresh failed:', err);
          setIsAuthenticated(false);
          setUser(null);
        }
      }, refreshIn);
    } catch {
      // No valid session, skip scheduling
    }
  }, [buildUserObject]);

  /**
   * Check for existing session on mount
   */
  useEffect(() => {
    const checkSession = async () => {
      try {
        await getCurrentUser();
        const userObj = await buildUserObject();
        if (userObj) {
          setUser(userObj);
          setIsAuthenticated(true);
          identifyPostHogUser(userObj);
          scheduleTokenRefresh();
        }
      } catch {
        // No current user / no valid session
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    return () => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [buildUserObject, scheduleTokenRefresh]);

  /**
   * Sign in with phone number and password
   */
  const signIn = useCallback(async (phoneNumber, password) => {
    try {
      setError(null);
      const result = await amplifySignIn({
        username: phoneNumber,
        password,
      });

      if (result.isSignedIn) {
        const userObj = await buildUserObject();
        setUser(userObj);
        setIsAuthenticated(true);
        identifyPostHogUser(userObj);
        scheduleTokenRefresh();
        return { success: true };
      }

      // Handle challenges (e.g., NEW_PASSWORD_REQUIRED, SMS_OTP)
      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [buildUserObject, identifyPostHogUser, scheduleTokenRefresh]);

  /**
   * Passwordless sign in - sends SMS OTP code to the phone number
   */
  const signInPasswordless = useCallback(async (phoneNumber) => {
    try {
      setError(null);
      const result = await amplifySignIn({
        username: phoneNumber,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'SMS_OTP',
        },
      });

      if (result.isSignedIn) {
        const userObj = await buildUserObject();
        setUser(userObj);
        setIsAuthenticated(true);
        identifyPostHogUser(userObj);
        scheduleTokenRefresh();
        return { success: true };
      }

      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [buildUserObject, identifyPostHogUser, scheduleTokenRefresh]);

  /**
   * Confirm sign-in challenge (e.g., SMS_OTP code for passwordless)
   */
  const confirmSignIn = useCallback(async (code) => {
    try {
      setError(null);
      const result = await amplifyConfirmSignIn({
        challengeResponse: code,
      });

      if (result.isSignedIn) {
        const userObj = await buildUserObject();
        setUser(userObj);
        setIsAuthenticated(true);
        identifyPostHogUser(userObj);
        scheduleTokenRefresh();
        return { success: true };
      }

      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [buildUserObject, identifyPostHogUser, scheduleTokenRefresh]);

  /**
   * Sign up with phone number, password, first name, last name
   */
  const signUp = useCallback(async (phoneNumber, password, firstName, lastName) => {
    try {
      setError(null);
      const result = await amplifySignUp({
        username: phoneNumber,
        password,
        options: {
          userAttributes: {
            phone_number: phoneNumber,
            given_name: firstName,
            family_name: lastName,
          },
          autoSignIn: true,
        },
      });

      return {
        success: true,
        isSignUpComplete: result.isSignUpComplete,
        nextStep: result.nextStep,
        userId: result.userId,
      };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Complete the Cognito auto sign-in flow after sign-up confirmation
   */
  const autoSignIn = useCallback(async () => {
    try {
      setError(null);
      const result = await amplifyAutoSignIn();

      if (result.isSignedIn) {
        const userObj = await buildUserObject();
        setUser(userObj);
        setIsAuthenticated(true);
        identifyPostHogUser(userObj);
        scheduleTokenRefresh();
        return { success: true };
      }

      return { success: false, nextStep: result.nextStep };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, [buildUserObject, identifyPostHogUser, scheduleTokenRefresh]);

  /**
   * Confirm sign up with SMS verification code
   */
  const confirmSignUp = useCallback(async (phoneNumber, code) => {
    try {
      setError(null);
      const result = await amplifyConfirmSignUp({
        username: phoneNumber,
        confirmationCode: code,
      });

      return { success: result.isSignUpComplete };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Resend sign-up verification code
   */
  const resendSignUpCode = useCallback(async (phoneNumber) => {
    try {
      setError(null);
      await amplifyResendSignUpCode({ username: phoneNumber });
      return { success: true };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Initiate forgot password flow (sends SMS code)
   */
  const forgotPassword = useCallback(async (phoneNumber) => {
    try {
      setError(null);
      const result = await amplifyResetPassword({ username: phoneNumber });
      return { success: true, nextStep: result.nextStep };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Confirm password reset with code and new password
   */
  const confirmForgotPassword = useCallback(async (phoneNumber, code, newPassword) => {
    try {
      setError(null);
      await amplifyConfirmResetPassword({
        username: phoneNumber,
        confirmationCode: code,
        newPassword,
      });
      return { success: true };
    } catch (err) {
      setError(err);
      throw err;
    }
  }, []);

  /**
   * Sign out
   */
  const signOut = useCallback(async () => {
    try {
      setError(null);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
      await amplifySignOut();
      posthog.reset();
      setUser(null);
      setIsAuthenticated(false);
    } catch (err) {
      setError(err);
      // Force clean state even on error
      posthog.reset();
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  /**
   * Get access token for API calls
   */
  const getAccessToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.accessToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  /**
   * Get ID token (contains user attributes)
   */
  const getIdToken = useCallback(async () => {
    try {
      const session = await fetchAuthSession();
      return session?.tokens?.idToken?.toString() || null;
    } catch {
      return null;
    }
  }, []);

  const value = useMemo(() => ({
    // State
    user,
    isAuthenticated,
    isLoading,
    error,

    // Auth actions
    signIn,
    signInPasswordless,
    confirmSignIn,
    signUp,
    confirmSignUp,
    autoSignIn,
    resendSignUpCode,
    signOut,
    forgotPassword,
    confirmForgotPassword,

    // Token access
    getAccessToken,
    getIdToken,
  }), [
    user, isAuthenticated, isLoading, error,
    signIn, signInPasswordless, confirmSignIn,
    signUp, confirmSignUp, autoSignIn, resendSignUpCode, signOut,
    forgotPassword, confirmForgotPassword,
    getAccessToken, getIdToken,
  ]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default CognitoAuthProvider;
